<template>
  <div class="py-4 p-md-4 align-content-center d-flex">
    <a v-if="!errored && showOpenExternallyButton" class="link external" target="new" :href="zoomSrc" title="Öppna i ny flik">
      <i class="fa fa-external-link" />
    </a>
    <div
      v-if="imgUrl"
      ref="zoomableImage"
      class="zoomable-image no-zoom"
      :class="{ square }"
      :style="`cursor: ${!zoomSrc || errored ? 'default' : zoomActive ? 'zoom-out' : 'zoom-in'}; background-image: url('${imgUrl}')`"
      @click="isTouchDevice || !zoomSrc ? 0 : toggleZoom($event)"
    />
    <!-- used for error check -->
    <img id="dummy-img" class="d-none" :src="previewSrc" @error.once="errored = true" @load="handleImgLoad" />
  </div>
</template>

<script lang="ts" setup>
import { isTouchDevice } from '@/models/Global'
import { LegacyProductFallbackImage, ProductFallbackImage } from '@/models/Image'
import { clamp } from '@/utils/tools'
import { computed, ref } from 'vue'

// -----------
// -- Setup --
// -----------
const props = withDefaults(
  defineProps<{
    previewSrc?: string
    zoomSrc?: string
    minZoom?: number
    defaultZoom?: number
    maxZoom?: number
    showOpenExternallyButton?: boolean
    fallbackImg?: typeof ProductFallbackImage | typeof LegacyProductFallbackImage
    square?: boolean
    preloadZoomableImg?: boolean
  }>(),
  { minZoom: 0.5, defaultZoom: 0.5, maxZoom: 2, fallbackImg: ProductFallbackImage },
)

const zoomDelta = 0.2

// ---------------
// -- Reactives --
// ---------------
const zoomLevel = ref(props.defaultZoom)
const zoomActive = ref(false)
const errored = ref(false)
const zoomPreloadImage = ref(new Image())
const zoomableImage = ref<HTMLElement>()

const imgUrl = computed(() => (errored.value ? props.fallbackImg : zoomActive.value ? props.zoomSrc : props.previewSrc))

// ---------------
// -- Handlers --
// ---------------

const handleImgLoad = () => {
  if (props.zoomSrc && props.preloadZoomableImg) {
    zoomPreloadImage.value.src = props.zoomSrc // set zoom source when preview has loaded, ensuring the preview is loaded before the zoom img starts loading
  }
}

const moveZoomFocusPointListener = (e: any) => {
  setZoomFocusPoint(e)
}

const changeZoomMagnitudeListener = (e: any) => {
  e.preventDefault()
  zoomLevel.value = clamp(zoomLevel.value + (e.wheelDeltaY > 0 ? zoomDelta : -zoomDelta), props.minZoom, props.maxZoom)
  setZoomFocusPoint(e)
}

const blockEscListener = () => (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.stopPropagation()
    deactivateZoom()
  }
}

// ---------------
// -- Functions --
// ---------------
const toggleZoom = (e: MouseEvent) => {
  if (!errored.value) {
    if (zoomActive.value) {
      deactivateZoom()
    } else {
      activateZoom(e)
    }
  }
}

const deactivateZoom = () => {
  zoomableImage.value?.removeEventListener('mousemove', moveZoomFocusPointListener)
  // zoomableImage.value?.removeEventListener('mouseleave', deactivateZoom)
  zoomableImage.value?.removeEventListener('mousewheel', changeZoomMagnitudeListener)
  document.removeEventListener('keydown', blockEscListener, { capture: true })
  zoomActive.value = false
  zoomableImage.value?.classList.add('no-zoom')
}

const activateZoom = (e: MouseEvent) => {
  zoomPreloadImage.value.src = props.zoomSrc || ''

  zoomableImage.value?.classList.remove('no-zoom')

  zoomableImage.value?.addEventListener('mousemove', moveZoomFocusPointListener)
  // zoomableImage.value?.addEventListener('mouseleave', deactivateZoom)

  zoomableImage.value?.addEventListener('mousewheel', changeZoomMagnitudeListener, { passive: false })
  document.addEventListener('keydown', blockEscListener, { capture: true })

  zoomActive.value = true
  setTimeout(() => setZoomFocusPoint(e), 0)
}

const setZoomFocusPoint = (e: any) => {
  const rect = e.target.getBoundingClientRect()

  const xPos = e.clientX - rect.left
  const yPos = e.clientY - rect.top

  const xPercent = (xPos / rect.width) * 100
  const yPercent = (yPos / rect.height) * 100

  if (zoomableImage.value) {
    const zoomStyle: Partial<CSSStyleDeclaration> = {
      backgroundPosition: `${xPercent}% ${yPercent}%`,
      backgroundSize: `${zoomPreloadImage.value.naturalWidth * zoomLevel.value}px ${zoomPreloadImage.value.naturalHeight * zoomLevel.value}px`,
      backgroundOrigin: 'border-box',
    }

    Object.assign(zoomableImage.value?.style, zoomStyle)
  }
}
</script>

<style lang="scss" scoped>
.content-box {
  display: flex;
  justify-content: center;
}

.zoomable-image {
  width: 100%;
  background-repeat: no-repeat !important;

  &.square {
    padding-top: 100% !important;
  }
}

.no-zoom {
  background-size: contain !important;
  background-position: center !important;
}

.external {
  position: absolute;
  padding: 0.5em;

  i {
    font-size: 1.3em;
  }
}
</style>
